/**
 * CR-8720: the bounds on an NCR claim's money and quantity inputs.
 *
 * The server enforces the same rules (`quality/services/ncr_claim_limits.py`);
 * these helpers let the form say the number before it is sent. The NCR detail
 * carries `credit_currency_default`, `credit_amount_limit` and
 * `replacement_qty_limit`; a null limit means no cap is known.
 */

/** Money: 0.00 or more, at most two decimals, within the order value. */
export interface MoneyRule {
  /** ISO code named in messages, e.g. 'GBP'. */
  currency: string;
  /** The order value as the API sends it; null = no cap. */
  limit: string | null | undefined;
}

/**
 * Validate a money string the way the server will.
 *
 * @param raw - the input's value
 * @param rule - currency and cap
 * @param label - the field's name in the message, e.g. 'Credit amount'
 * @returns the message to show, or null when the value is acceptable (blank is acceptable)
 */
export function moneyError(raw: string, rule: MoneyRule, label: string): string | null {
  const text = raw.trim();
  if (!text) return null;
  const value = Number(text);
  if (!Number.isFinite(value)) return `Enter the ${lowerFirst(label)} as a number, e.g. 12.50.`;
  if (value < 0) return `${label} can’t be negative.`;
  if (!/^\d*(\.\d{0,2})?$/.test(text)) return `${label} can have at most 2 decimal places.`;
  const cap = rule.limit != null ? Number(rule.limit) : NaN;
  if (Number.isFinite(cap) && value > cap) {
    return `${label} can’t exceed the order value of ${rule.currency} ${formatMoney(cap)}.`;
  }
  return null;
}

/**
 * Validate a whole-number quantity the way the server will.
 *
 * @param raw - the input's value
 * @param limit - the ordered quantity as the API sends it; null = no cap
 * @param label - the field's name in the message, e.g. 'Quantity dispatched'
 * @returns the message to show, or null when acceptable (blank is acceptable)
 */
export function quantityError(raw: string, limit: string | null | undefined, label: string): string | null {
  const text = raw.trim();
  if (!text) return null;
  const value = Number(text);
  if (!Number.isFinite(value)) return `Enter the ${lowerFirst(label)} as a number.`;
  if (value < 0) return `${label} can’t be negative.`;
  if (!Number.isInteger(value)) return `${label} must be a whole number.`;
  const cap = limit != null ? Number(limit) : NaN;
  if (Number.isFinite(cap) && value > cap) {
    return `${label} can’t exceed the ordered quantity of ${formatQty(cap)}.`;
  }
  return null;
}

/**
 * A money string trimmed to two decimals (for the input's blur), untouched
 * when blank or not a number so the error can still be shown.
 *
 * @param raw - the input's value
 * @returns the value with exactly two decimals, or `raw`
 */
export function normaliseMoney(raw: string): string {
  const text = raw.trim();
  if (!text) return raw;
  const value = Number(text);
  if (!Number.isFinite(value) || value < 0) return raw;
  return (Math.round(value * 100) / 100).toFixed(2);
}

/**
 * The cap, worded for a hint under the input.
 *
 * @param rule - currency and cap
 * @returns e.g. 'Up to GBP 1,250.00 (order value)', or null when no cap is known
 */
export function moneyHint(rule: MoneyRule): string | null {
  const cap = rule.limit != null ? Number(rule.limit) : NaN;
  return Number.isFinite(cap) ? `Up to ${rule.currency} ${formatMoney(cap)} (order value)` : null;
}

/**
 * The quantity cap, worded for a hint under the input.
 *
 * @param limit - the ordered quantity as the API sends it
 * @returns e.g. 'Up to 30 (ordered quantity)', or null when no cap is known
 */
export function quantityHint(limit: string | null | undefined): string | null {
  const cap = limit != null ? Number(limit) : NaN;
  return Number.isFinite(cap) ? `Up to ${formatQty(cap)} (ordered quantity)` : null;
}

/**
 * Is a YYYY-MM-DD date before today (the viewer's calendar day)?
 *
 * @param iso - the date input's value
 * @param today - today as YYYY-MM-DD
 * @returns true when `iso` is earlier than `today`
 */
export function isPastDate(iso: string, today: string): boolean {
  return !!iso && iso < today;
}

function formatMoney(value: number): string {
  return value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatQty(value: number): string {
  return value.toLocaleString('en-GB', { maximumFractionDigits: 3 });
}

function lowerFirst(label: string): string {
  return label.charAt(0).toLowerCase() + label.slice(1);
}
